import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  ExcelExport,
  PdfExport,
  FilterType,
} from '@syncfusion/ej2-react-grids';

import { useGetRefundApprovalsByUserQuery, RefundView } from '../../../../../services/RefundService';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { useApprovalsSearch, useApprovalsNavigation } from './hooks';
import { SearchBar } from './components';
import {
  PatientTemplate,
  AccountTemplate,
  RefundIdTemplate,
  DateTemplate,
  RefundStatusChipTemplate,
  PriorityChipTemplate,
} from './templates';

export interface MyApprovalsGridProps {
  testId?: string;
}

// Grid settings
const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
const filterSettings = { type: 'Excel' as FilterType };
const sortSettings = { allowUnsort: false };

// Grid styling
const gridContainerStyles = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

/**
 * MyApprovalsGrid - Displays refunds requiring user's approval
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search and navigation
 * - Reusable search bar component
 */
export const MyApprovalsGrid: React.FC<MyApprovalsGridProps> = ({ testId }) => {
  const selectedCenter = useAppSelector((state) => state.center.selectedCenter);
  const { data: profile } = useGetMyProfileQuery();
  const company = profile?.organization || '';
  const userName = profile?.name || '';

  // Fetch my approvals data
  const {
    data: approvalsItems = [],
    isLoading,
    error,
  } = useGetRefundApprovalsByUserQuery(
    {
      center: selectedCenter || '',
      company,
      userName,
    },
    {
      skip: !selectedCenter || !company || !userName,
    }
  );

  // Use custom hooks
  const { searchTerm, filteredItems, handleSearchChange, handleClearSearch } =
    useApprovalsSearch(approvalsItems);
  const { handleRowSelected } = useApprovalsNavigation(company, selectedCenter || '');

  // Create templates
  const patientTemplate = useCallback(
    (rowData: RefundView) => (
      <PatientTemplate firstName={rowData.firstName} lastName={rowData.lastName} />
    ),
    []
  );

  const accountTemplate = useCallback(
    (rowData: RefundView) => <AccountTemplate accountNumber={rowData.accountNumber} />,
    []
  );

  const refundIdTemplate = useCallback(
    (rowData: RefundView) => <RefundIdTemplate id={rowData.id} />,
    []
  );

  const refundStartDateTemplate = useCallback(
    (rowData: RefundView) => <DateTemplate date={rowData.refundStartDate} />,
    []
  );

  const refundStatusTemplate = useCallback(
    (rowData: RefundView) => <RefundStatusChipTemplate status={rowData.status} />,
    []
  );

  const priorityTemplate = useCallback(
    (rowData: RefundView) => <PriorityChipTemplate priority={rowData.priority} />,
    []
  );

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading my approvals...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading my approvals. Please try again.</Typography>
      </Box>
    );
  }

  // Handle empty results
  if (approvalsItems.length === 0) {
    return (
      <Box>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClear={handleClearSearch}
        />
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No approvals found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No refunds requiring your approval in {selectedCenter}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredItems.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClear={handleClearSearch}
        />
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No approvals match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClear={handleClearSearch}
      />

      {/* Results Summary */}
      {searchTerm.trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredItems.length} of {approvalsItems.length} approvals
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={filteredItems}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          allowExcelExport={true}
          allowPdfExport={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          height="500px"
          rowSelected={handleRowSelected}
          data-testid={testId}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="patient"
              headerText="Patient"
              width="150"
              textAlign="Left"
              template={patientTemplate}
            />
            <ColumnDirective
              field="accountNumber"
              headerText="Account"
              width="120"
              textAlign="Left"
              template={accountTemplate}
            />
            <ColumnDirective
              field="id"
              headerText="Refund ID"
              width="100"
              textAlign="Left"
              template={refundIdTemplate}
            />
            <ColumnDirective
              field="status"
              headerText="Status"
              width="120"
              textAlign="Center"
              template={refundStatusTemplate}
            />
            <ColumnDirective
              field="priority"
              headerText="Priority"
              width="100"
              textAlign="Center"
              template={priorityTemplate}
            />
            <ColumnDirective
              field="refundStartDate"
              headerText="Start Date"
              width="120"
              textAlign="Center"
              template={refundStartDateTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default MyApprovalsGrid;
